import { useMemo, useState } from 'react';
import Modal from '@/components/ui/Modal';
import { socialLogin, socialRegister } from '@/lib/api/auth';
import { ForumSession, saveForumSession } from '@/lib/forumClient';

type AuthMode = 'login' | 'signup';

type ForumAuthModalProps = {
  open: boolean;
  initialMode?: AuthMode;
  onClose: () => void;
  onAuthenticated: (session: ForumSession) => void;
};

type AuthFormState = {
  username: string;
  password: string;
};

const defaultFormState: AuthFormState = {
  username: '',
  password: '',
};

const USERNAME_MAX_LENGTH = 60;
const PASSWORD_MAX_LENGTH = 128;

const getSessionFromResponse = (
  payload: any,
  fallback?: Partial<ForumSession>,
): ForumSession => {
  const data = payload?.data || payload?.result || payload?.user || payload;
  const username =
    data?.username || data?.user_name || data?.name || fallback?.username || '';
  const email = data?.email || fallback?.email || '';
  const id = data?.id || data?.user_id || fallback?.id;
  const token = data?.token || payload?.token;

  return {
    id,
    username,
    firstName:
      data?.first_name || data?.firstName || fallback?.firstName || username,
    lastName: data?.last_name || data?.lastName || fallback?.lastName || '',
    email,
    token,
  };
};

const Field = ({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  maxLength,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoComplete?: string;
  maxLength?: number;
}) => (
  <label className='block'>
    <span className='mb-2 block text-sm font-semibold text-[#333533]'>
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      maxLength={maxLength}
      className='w-full rounded-2xl border border-[#333533]/16 bg-[#fffdf3] px-4 py-3 text-sm text-[#202020] outline-none transition placeholder:text-[#7a786d] focus:border-[#00e5ff] focus:bg-white'
    />
  </label>
);

const ForumAuthModal = ({
  open,
  initialMode = 'login',
  onClose,
  onAuthenticated,
}: ForumAuthModalProps) => {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [loginForm, setLoginForm] = useState(defaultFormState);
  const [signupForm, setSignupForm] = useState(defaultFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const modalTitle = useMemo(
    () => (mode === 'login' ? 'Log in to continue' : 'Create your account'),
    [mode],
  );

  const handleModeChange = (nextMode: AuthMode) => {
    setMode(nextMode);
    setError('');
  };

  const finishAuth = (payload: any, fallback?: Partial<ForumSession>) => {
    const session = getSessionFromResponse(payload, fallback);
    saveForumSession(session);
    onAuthenticated(session);
    setError('');
    setLoginForm(defaultFormState);
    setSignupForm(defaultFormState);
  };

  const submitLogin = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');

    if (
      !loginForm.username.trim() ||
      !loginForm.password ||
      loginForm.username.trim().length > USERNAME_MAX_LENGTH ||
      loginForm.password.length > PASSWORD_MAX_LENGTH
    ) {
      setError('Enter your username and password.');
      return;
    }

    setLoading(true);
    try {
      const payload = await socialLogin(loginForm);
      finishAuth(payload, { username: loginForm.username.trim() });
    } catch (err: any) {
      setError(err?.message || 'Unable to log in right now.');
    } finally {
      setLoading(false);
    }
  };

  const submitSignup = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');

    if (
      !signupForm.username.trim() ||
      !signupForm.password ||
      signupForm.username.trim().length > USERNAME_MAX_LENGTH ||
      signupForm.password.length > PASSWORD_MAX_LENGTH
    ) {
      setError('Enter your username and password.');
      return;
    }

    setLoading(true);
    try {
      const signupPayload = await socialRegister(signupForm);
      const hasToken = Boolean(
        signupPayload?.token || signupPayload?.data?.token,
      );

      if (hasToken) {
        finishAuth(signupPayload, { username: signupForm.username.trim() });
      } else {
        const loginPayload = await socialLogin(signupForm);
        finishAuth(loginPayload, { username: signupForm.username.trim() });
      }
    } catch (err: any) {
      setError(err?.message || 'Unable to create the account right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={modalTitle}>
      <div className='overflow-hidden rounded-[1.75rem] border border-[#333533]/14 bg-[linear-gradient(180deg,#fffef7_0%,#f0ede2_100%)]'>
        <div className='rounded-[1.75rem] bg-[linear-gradient(135deg,#202020_0%,#333533_100%)] px-6 py-6 text-white'>
          <div className='mt-4 text-xs font-bold uppercase tracking-[0.22em] text-[#9bf6ff]/85'>
            {mode === 'login' ? 'Welcome Back' : 'Registration'}
          </div>
          <h3 className='mt-2 text-2xl font-black tracking-tight text-white'>
            {mode === 'login'
              ? 'Log in to post or comment'
              : 'Create your account'}
          </h3>
          <p className='mt-2 text-sm leading-6 text-white/80'>
            {mode === 'login'
              ? 'Enter your username and password to continue into the forum.'
              : 'Create a simple forum account with a username and password.'}
          </p>
        </div>

        <div className='p-6'>
          <div className='mb-5 flex rounded-2xl bg-[#e3dfcf] p-1'>
            <button
              type='button'
              onClick={() => handleModeChange('login')}
              className={`flex-1 rounded-[0.9rem] px-4 py-3 text-sm font-bold transition ${
                mode === 'login'
                  ? 'bg-[#202020] text-[#9bf6ff] shadow-sm'
                  : 'text-[#333533]'
              }`}
            >
              Login
            </button>
            <button
              type='button'
              onClick={() => handleModeChange('signup')}
              className={`flex-1 rounded-[0.9rem] px-4 py-3 text-sm font-bold transition ${
                mode === 'signup'
                  ? 'bg-[#202020] text-[#9bf6ff] shadow-sm'
                  : 'text-[#333533]'
              }`}
            >
              Sign up
            </button>
          </div>

          {mode === 'login' ? (
            <form className='space-y-4' onSubmit={submitLogin}>
              <Field
                label='Username'
                value={loginForm.username}
                onChange={(username) =>
                  setLoginForm((current) => ({ ...current, username }))
                }
                placeholder='Choose your username'
                autoComplete='username'
                maxLength={USERNAME_MAX_LENGTH}
              />
              <Field
                label='Password'
                type='password'
                value={loginForm.password}
                onChange={(password) =>
                  setLoginForm((current) => ({ ...current, password }))
                }
                placeholder='Enter your password'
                autoComplete='current-password'
                maxLength={PASSWORD_MAX_LENGTH}
              />
              {error && (
                <div className='rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700'>
                  {error}
                </div>
              )}
              <button
                type='submit'
                disabled={loading}
                className='w-full rounded-2xl bg-[#202020] px-4 py-3 text-sm font-black text-[#9bf6ff] transition hover:bg-[#333533] disabled:cursor-not-allowed disabled:bg-[#d6d6d6] disabled:text-[#202020]'
              >
                {loading ? 'Signing in...' : 'Log in'}
              </button>
            </form>
          ) : (
            <form className='space-y-4' onSubmit={submitSignup}>
              <Field
                label='Username'
                value={signupForm.username}
                onChange={(username) =>
                  setSignupForm((current) => ({ ...current, username }))
                }
                placeholder='Choose your username'
                autoComplete='username'
                maxLength={USERNAME_MAX_LENGTH}
              />
              <Field
                label='Password'
                type='password'
                value={signupForm.password}
                onChange={(password) =>
                  setSignupForm((current) => ({ ...current, password }))
                }
                placeholder='Create your password'
                autoComplete='new-password'
                maxLength={PASSWORD_MAX_LENGTH}
              />
              {error && (
                <div className='rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700'>
                  {error}
                </div>
              )}
              <button
                type='submit'
                disabled={loading}
                className='w-full rounded-2xl bg-[#202020] px-4 py-3 text-sm font-black text-[#9bf6ff] transition hover:bg-[#333533] disabled:cursor-not-allowed disabled:bg-[#d6d6d6] disabled:text-[#202020]'
              >
                {loading ? 'Creating account...' : 'Create account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ForumAuthModal;
